/**
 * Every rule the table can argue about, as data.
 *
 * Risk is house-ruled everywhere, and the app's job is to keep score, not to
 * insist on one edition. So the reducer and validators read from a config
 * rather than hardcoding a ruleset, and the two published editions ship as
 * presets built from the same knobs.
 */

export type CardSymbol = 'infantry' | 'cavalry' | 'artillery' | 'wild';

/** The named shapes a valid three-card set can take. */
export type SetType = 'infantry' | 'cavalry' | 'artillery' | 'mixed' | 'wild';

export type CardProgression =
  /** Classic: 4, 6, 8, 10, 12, 15, then +increment forever. */
  | { readonly kind: 'escalating'; readonly sequence: readonly number[]; readonly increment: number }
  /** 2008 revised: the value depends on the set's shape, not on how many have been traded. */
  | { readonly kind: 'fixed'; readonly values: Readonly<Record<SetType, number>> };

export interface CardRules {
  readonly progression: CardProgression;
  /** Hand size at which a trade becomes compulsory. */
  readonly forceTradeAt: number;
  /** Extra armies for owning a territory shown on a traded card. */
  readonly territoryBonus: number;
  /** How many cards in one trade may earn that bonus. */
  readonly maxBonusCardsPerTrade: number;
  /** Wild cards in the deck. Only used to sanity-check mirrored hands. */
  readonly wildCards: number;
}

export interface ReinforcementRules {
  /** Territories are divided by this. */
  readonly divisor: number;
  /** Floor on the per-turn draft, however few territories you hold. */
  readonly minimum: number;
  /** Overrides the map's own continent bonuses when a table disagrees. */
  readonly continentBonusOverrides: Readonly<Record<string, number>>;
  readonly capitalBonus: number;
}

export interface CombatRules {
  readonly maxAttackDice: number;
  readonly maxDefendDice: number;
  /** Minimum armies that must advance into a conquered territory. */
  readonly occupation: 'minDiceRolled' | 'atLeastOne' | 'allButOne';
  /**
   * Who types in dice results. At a real table the defender rolls their own,
   * but tables in a hurry let the active player enter everything.
   */
  readonly diceEntry: 'activePlayerOnly' | 'eachRollsOwn';
  /** Skip asking the defender how many dice, and always roll the maximum. */
  readonly defenderAlwaysRollsMax: boolean;
}

export interface FortifyRules {
  /** Adjacent-only, or any path through territories you own. */
  readonly style: 'adjacent' | 'connectedPath';
  /** Fortifying moves allowed per turn. */
  readonly movesPerTurn: number;
}

export interface EliminationRules {
  /** Does the eliminator take the victim's cards? */
  readonly captureCards: boolean;
  /** Where armies from a compulsory mid-turn trade may be placed. */
  readonly midTurnTradePlacement: 'any' | 'conqueredTerritory';
}

export interface SetupRules {
  /** Starting armies by player count. */
  readonly startingArmies: Readonly<Record<number, number>>;
  /** Two-player games use a neutral third army in the classic rules. */
  readonly neutralForTwoPlayer: boolean;
  readonly neutralDefense: 'opponentRolls' | 'app';
}

export interface VictoryRules {
  readonly condition: 'conquest' | 'objective' | 'capitals';
}

export interface RulesConfig {
  readonly cards: CardRules;
  readonly reinforcement: ReinforcementRules;
  readonly combat: CombatRules;
  readonly fortify: FortifyRules;
  readonly elimination: EliminationRules;
  readonly setup: SetupRules;
  readonly victory: VictoryRules;
  /**
   * Warn the second player to claim a card someone else already holds.
   * Naming the other holder would leak a hand, so the warning is anonymous.
   */
  readonly duplicateCardWarning: 'off' | 'private';
}

const STANDARD_STARTING_ARMIES: Readonly<Record<number, number>> = Object.freeze({
  2: 40,
  3: 35,
  4: 30,
  5: 25,
  6: 20,
});

/** Standard US rules, the version most people mean by "Risk". */
export const CLASSIC_1993: RulesConfig = Object.freeze({
  cards: {
    progression: { kind: 'escalating', sequence: [4, 6, 8, 10, 12, 15], increment: 5 },
    forceTradeAt: 5,
    territoryBonus: 2,
    maxBonusCardsPerTrade: 1,
    wildCards: 2,
  },
  reinforcement: {
    divisor: 3,
    minimum: 3,
    continentBonusOverrides: {},
    capitalBonus: 0,
  },
  combat: {
    maxAttackDice: 3,
    maxDefendDice: 2,
    occupation: 'minDiceRolled',
    diceEntry: 'activePlayerOnly',
    defenderAlwaysRollsMax: true,
  },
  fortify: { style: 'connectedPath', movesPerTurn: 1 },
  elimination: { captureCards: true, midTurnTradePlacement: 'any' },
  setup: {
    startingArmies: STANDARD_STARTING_ARMIES,
    neutralForTwoPlayer: true,
    neutralDefense: 'opponentRolls',
  },
  victory: { condition: 'conquest' },
  duplicateCardWarning: 'private',
} satisfies RulesConfig);

/** 2008 revised edition: fixed set values rather than an escalating ladder. */
export const REVISED_2008: RulesConfig = Object.freeze({
  ...CLASSIC_1993,
  cards: {
    ...CLASSIC_1993.cards,
    progression: {
      kind: 'fixed',
      values: { infantry: 4, cavalry: 6, artillery: 8, mixed: 10, wild: 12 },
    },
  },
  fortify: { style: 'adjacent', movesPerTurn: 1 },
  victory: { condition: 'objective' },
} satisfies RulesConfig);

export const RULES_PRESETS = Object.freeze({
  classic1993: CLASSIC_1993,
  revised2008: REVISED_2008,
});

export type RulesPresetName = keyof typeof RULES_PRESETS;
